import api from '../../../../api/client';

// ── Static filter options matching legacy DAL filter params ──

export const GENDER_OPTIONS = [
  { value: 'S', label: 'All Genders' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

export const DATE_PRESET_OPTIONS = [
  { value: 'This Month', label: 'This Month' },
  { value: 'Last Month', label: 'Last Month' },
  { value: 'Today', label: 'Today' },
  { value: 'Yesterday', label: 'Yesterday' },
  { value: 'Custom', label: 'Custom Range' },
];

/**
 * Map a backend biometric record row to the frontend model.
 * Backend columns (from legacy CTE): ID, EmpID, IDCardNo, Name, Description,
 * ADate (DD/MM/YYYY), FirstSwipe (HH:MM), LastSwipe (HH:MM),
 * ManualPunch ('M' or ''), TotalSwipes, Attendance.
 */
function mapBioItem(b) {
  return {
    id: b.ID ?? b.id ?? null,
    empId: b.EmpID ?? b.emp_id ?? null,
    idCardNo: b.IDCardNo ?? b.id_card_no ?? '',
    name: b.Name ?? b.name ?? '',
    // Description = "CompanyShortName-DeptName-BranchShortName"
    description: b.Description ?? b.description ?? '',
    // ADate comes as DD/MM/YYYY from CONVERT(VARCHAR(10), ADate, 103)
    aDate: b.ADate ?? b.a_date ?? '',
    firstSwipe: b.FirstSwipe ?? b.first_swipe ?? '',
    lastSwipe: b.LastSwipe ?? b.last_swipe ?? '',
    // 'M' = has manual punch component; '' = device swipes only
    manualPunch: b.ManualPunch ?? b.manual_punch ?? '',
    totalSwipes: b.TotalSwipes ?? b.total_swipes ?? 0,
    attendance: b.Attendance ?? b.attendance ?? '',
  };
}

export const biometricsApi = {
  /**
   * Fetch biometric records list with legacy filters.
   * Maps to GET /payroll/biometrics/
   */
  getRecords: async (filters = {}, page = 1, pageSize = 50) => {
    const params = {
      emp_id: filters.empId && filters.empId !== 'All' ? Number(filters.empId) : undefined,
      company_id: filters.companyId && filters.companyId !== 0 ? Number(filters.companyId) : undefined,
      branch_id: filters.branchId && filters.branchId !== 0 ? Number(filters.branchId) : undefined,
      dept_id: filters.deptId && filters.deptId !== 0 ? Number(filters.deptId) : undefined,
      gender: filters.gender && filters.gender !== 'S' && filters.gender !== 'All'
        ? filters.gender
        : undefined,
      emp_group_id: filters.empGroupId && filters.empGroupId !== 0 ? Number(filters.empGroupId) : undefined,
      from_date: filters.fromDate || undefined,
      to_date: filters.toDate || undefined,
      search: filters.search || undefined,
      page,
      page_size: pageSize,
    };

    const { data } = await api.get('/payroll/biometrics/', { params });
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items: items.map(mapBioItem),
      total: data?.total ?? 0,
      page: data?.page ?? page,
      page_size: data?.page_size ?? pageSize,
    };
  },

  /**
   * Get individual swipe detail for an employee on a specific date.
   * Maps to GET /payroll/biometrics/swipe-detail?emp_id=&a_date=
   */
  getSwipeDetail: async (empId, aDate) => {
    // aDate may come as DD/MM/YYYY (from backend), convert to YYYY-MM-DD
    let isoDate = aDate;
    if (aDate && aDate.includes('/')) {
      const parts = aDate.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        isoDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    const { data } = await api.get('/payroll/biometrics/swipe-detail', {
      params: { emp_id: empId, a_date: isoDate },
    });
    return data?.items || [];
  },

  /**
   * Manually add a swipe card entry (BiometricRecordsDAL.SaveSwipe).
   * Maps to POST /payroll/biometrics/swipe
   */
  saveSwipe: async (payload) => {
    const body = {
      gr_no: Number(payload.gr_no || payload.grNo),
      t_datetime: payload.t_datetime || payload.tDatetime,
      device_id: payload.device_id || 0,
      remark: payload.remark || '',
    };
    const { data } = await api.post('/payroll/biometrics/swipe', body);
    return data;
  },

  /**
   * Delete a SwipeCard record (BiometricRecordsDAL.DeleteSwipe).
   * Maps to DELETE /payroll/biometrics/swipe/{id}
   */
  deleteSwipe: async (swipeId) => {
    const { data } = await api.delete(`/payroll/biometrics/swipe/${swipeId}`);
    return data;
  },

  /**
   * Download the Excel bulk upload template.
   * Maps to GET /payroll/biometrics/template
   */
  downloadTemplate: async () => {
    const response = await api.get('/payroll/biometrics/template', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'biometric_swipe_template.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Bulk upload biometric swipes from an Excel file.
   * Maps to POST /payroll/biometrics/upload
   */
  uploadBiometricFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/payroll/biometrics/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};
